<template lang="html">
  <div
    class="cart-item flex flex-wrap items-start gap-3 rounded-md border border-default bg-default p-3 sm:flex-nowrap sm:gap-4"
  >
    <UCheckbox
      v-if="props.selectable"
      :model-value="props.selected"
      size="xl"
      class="mt-1 self-start"
      @update:model-value="emit('update:selected', $event === true)"
    />

    <NuxtLink
      :to="link"
      class="cart-item__cover h-24 w-24 shrink-0 overflow-hidden rounded-md bg-elevated"
    >
      <UiPicture :src="cover?.id" variant="imageFull" />
    </NuxtLink>

    <div class="cart-item__info flex min-w-40 flex-1 flex-col gap-1.5 self-stretch py-0.5">
      <NuxtLink :to="link" class="text-sm font-bold hover:underline sm:text-base">
        {{ props.value?.wellKnownAttributes?.Name || 'Без названия' }}
      </NuxtLink>

      <PromoBadge
        :badge="props.value?.promoBadge"
        :cart-promo="props.value?.hasCartPromo && !props.value?.hasCashbackPromo"
        :cashback="props.value?.hasCashbackPromo"
      />

      <div v-if="keyAttrs" class="flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-muted">
        <span v-for="(attr, index) in keyAttrs" :key="index">
          {{ attr.displayName }}: {{ attr.value }}
        </span>
      </div>

      <span v-if="!props.value?.inStock" class="text-sm font-bold text-muted">
        * Нет в наличии
      </span>

      <PromoPrice
        v-if="props.value"
        :value="props.value"
        :unit-price="props.unitPrice"
        class="mt-auto text-sm"
      />
    </div>

    <div
      class="cart-item__side flex w-full shrink-0 items-center justify-between gap-3 sm:w-auto sm:flex-col sm:items-end sm:justify-between sm:self-stretch"
    >
      <div
        v-if="props.value?.inStock"
        class="flex items-center gap-1 rounded-md border border-default"
      >
        <UButton
          icon="i-lucide-minus"
          color="neutral"
          variant="ghost"
          size="sm"
          :disabled="isLoading || amount <= 1"
          @click="handleDecrease"
        />

        <AppLoader v-if="checkIsLoading('add') || checkIsLoading('remove')" class="size-4" />
        <span v-else class="min-w-5 text-center font-medium tabular-nums">
          {{ amount }}
        </span>

        <UButton
          icon="i-lucide-plus"
          color="neutral"
          variant="ghost"
          size="sm"
          :disabled="isLoading"
          @click="handleIncrease"
        />
      </div>

      <div class="flex items-center gap-2 sm:flex-col sm:items-end sm:gap-1">
        <span class="text-base font-bold tabular-nums sm:text-lg">
          {{ $f.currency(lineTotal) }}
        </span>

        <div class="flex gap-1">
          <UButton
            v-if="props.showNotify && !props.value?.inStock"
            :icon="
              props.value?.notifyInStock
                ? 'i-heroicons-bell-solid'
                : 'i-lucide-bell'
            "
            variant="ghost"
            size="sm"
            :class="props.value?.notifyInStock ? 'text-tertiary-500' : 'text-muted'"
            @click="emit('toggleNotify', props.value!.id!)"
          />

          <UButton
            icon="i-lucide-trash"
            variant="ghost"
            size="sm"
            class="text-muted"
            @click="handleRemove"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CartProductListItemDto } from '@repo/api/web/models'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import PromoBadge from '#Promotion/components/PromoBadge.vue'
import PromoPrice from '#Promotion/components/PromoPrice.vue'
import { getEffectiveCatalogPrice } from '#Promotion/utils/promoPrice'
import UiPicture from '~/app/components/Image/UiPicture.vue'
import AppLoader from '~/app/components/Loader/AppLoader.vue'

interface AttributeMeta {
  displayName?: string
  systemName?: string
  defaultConfig?: { type?: string }
}

const props = withDefaults(
  defineProps<{
    value?: CartProductListItemDto
    attributesMeta?: Record<string, object>
    selectable?: boolean
    selected?: boolean
    showNotify?: boolean
    unitPrice?: number
  }>(),
  {
    selectable: false,
    selected: false,
    showNotify: false,
  },
)
const emit = defineEmits<{
  remove: [productId: number]
  'update:selected': [value: boolean]
  toggleNotify: [productId: number]
}>()

const { routing } = useProducts()
const { addToCart, removeFromCart } = useCart()
const { send, isLoading, checkIsLoading } = useRequest()
const { $f } = useFormatter()
const ecommerce = useEcommerce()

const amount = computed(() => props.value?.amount || 1)

const unitPriceEffective = computed(() =>
  props.unitPrice ?? getEffectiveCatalogPrice(props.value ?? {}),
)
const lineTotal = computed(() => unitPriceEffective.value * amount.value)

async function handleIncrease() {
  if (!props.value?.id) return

  await send(() => addToCart(props.value!.id!, 1), { key: 'add' })
  ecommerce.add(mapCatalogProduct(props.value!, { quantity: 1 }))
}

async function handleDecrease() {
  if (!props.value?.id || amount.value <= 1) return

  await send(() => removeFromCart(props.value!.id!, 1), { key: 'remove' })
  ecommerce.remove(mapCatalogProduct(props.value!, { quantity: 1 }))
}

// Ecommerce: удаление всей строки корзины (remove) — используем полное кол-во позиции
function handleRemove() {
  if (!props.value) return
  ecommerce.remove(
    mapCatalogProduct(props.value, { quantity: props.value.amount || 1 }),
  )
  emit('remove', props.value.id!)
}

const cover = computed(() => {
  return props.value?.images?.at(0)
})
const link = computed(() => {
  return routing.getDetails(props.value as any)?.route
})
const keyAttrs = computed(() => {
  if (!props.value?.keyAttributesIds?.length) return

  return props.value.keyAttributesIds
    .map((id) => {
      const attrValues = props.value?.allAttributes?.[String(id)]
      const meta = props.attributesMeta?.[String(id)] as
        | AttributeMeta
        | undefined
      if (!attrValues?.length || !meta) return null

      const firstValue = attrValues[0]
      const displayValue = firstValue.displayName ?? firstValue.value

      return {
        displayName: meta.displayName as string,
        value: displayValue,
      }
    })
    .filter((item): item is NonNullable<typeof item> => item != null)
})
</script>
